// useRecipes — list + cached detail lookup. Server returns recipes
// WITHOUT items in `list`, so detail loads on demand via `get(id)`.

import { useCallback, useEffect, useState } from "preact/hooks";
import { api, isOfflineError, peekCache, type Recipe, type RecipeItem } from "./rpc";
import { sse } from "./sse";

export interface UseRecipes {
  recipes: Recipe[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  get: (id: string) => Promise<{ recipe: Recipe; items: RecipeItem[] } | null>;
  create: (name: string) => Promise<Recipe | null>;
  update: (id: string, patch: Partial<Pick<Recipe, "name" | "imageUrl" | "instructions" | "notes">>) => Promise<Recipe | null>;
  remove: (id: string) => Promise<void>;
  addItem: (recipeId: string, text: string, quantity?: string) => Promise<RecipeItem | null>;
  updateItem: (itemId: string, patch: Partial<RecipeItem>) => Promise<RecipeItem | null>;
  deleteItem: (itemId: string) => Promise<void>;
  setTags: (recipeId: string, tags: string[]) => Promise<Recipe | null>;
  retag: (recipeId: string) => Promise<void>;
}

export function useRecipes(): UseRecipes {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);

    // SWR: paint cached recipes immediately, then refresh from network.
    let hadCache = false;
    try {
      const cached = await peekCache<{ ok: true; recipes: Recipe[] }>("recipes.list");
      if (cached?.recipes?.length) {
        hadCache = true;
        setRecipes(cached.recipes);
        setLoading(false);
      }
    } catch { /* swallow */ }
    if (!hadCache) setLoading(true);

    try { setRecipes(await api.recipesList()); }
    catch (e: any) {
      if (!hadCache || !isOfflineError(e)) {
        setError(e?.message || "failed to load recipes");
      }
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Pick up background-classifier tag updates. The server emits
  // `recipe:tags.updated` after the LLM-based classifier finishes for
  // a freshly-imported/created recipe; we patch the matching row in
  // place so the filter chips reflect the new tags without a refetch.
  useEffect(() => {
    return sse.on((ev: any) => {
      if (ev?.type === "recipe:tags.updated" && ev.recipeId) {
        setRecipes((prev) => prev.map((r) =>
          String(r.id) === String(ev.recipeId) ? { ...r, tags: ev.tags || null } : r,
        ));
      }
    });
  }, []);

  const get = useCallback(async (id: string) => {
    try { return await api.recipesGet(id); }
    catch (e: any) { setError(e?.message || "failed to load recipe"); return null; }
  }, []);

  const create = useCallback(async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    try {
      const r = await api.recipesCreate(trimmed);
      setRecipes((prev) => [...prev, r]);
      return r;
    } catch (e: any) { setError(e?.message || "create failed"); return null; }
  }, []);

  const update = useCallback(async (id: string, patch: Partial<Pick<Recipe, "name" | "imageUrl" | "instructions" | "notes">>) => {
    setRecipes((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r));
    try {
      const r = await api.recipesUpdate(id, patch);
      setRecipes((prev) => prev.map((x) => x.id === id ? r : x));
      return r;
    } catch (e: any) { setError(e?.message || "update failed"); return null; }
  }, []);

  const remove = useCallback(async (id: string) => {
    setRecipes((prev) => prev.filter((r) => r.id !== id));
    try { await api.recipesDelete(id); }
    catch (e: any) { setError(e?.message || "delete failed"); }
  }, []);

  const addItem = useCallback(async (recipeId: string, text: string, quantity?: string) => {
    try { return await api.recipesAddItem(recipeId, text, quantity); }
    catch (e: any) { setError(e?.message || "add ingredient failed"); return null; }
  }, []);

  const updateItem = useCallback(async (itemId: string, patch: Partial<RecipeItem>) => {
    try { return await api.recipesUpdateItem(itemId, patch); }
    catch (e: any) { setError(e?.message || "update ingredient failed"); return null; }
  }, []);

  const deleteItem = useCallback(async (itemId: string) => {
    try { await api.recipesDeleteItem(itemId); }
    catch (e: any) { setError(e?.message || "delete ingredient failed"); }
  }, []);

  // Manual tag override. Optimistic — list updates immediately, server
  // confirms via the same recipe:tags.updated SSE the classifier uses,
  // so a concurrent edit on another device stays in sync.
  const setTags = useCallback(async (recipeId: string, tags: string[]) => {
    setRecipes((prev) => prev.map((r) => r.id === recipeId ? { ...r, tags } : r));
    try {
      return await api.recipesSetTags(recipeId, tags);
    } catch (e: any) { setError(e?.message || "set tags failed"); return null; }
  }, []);

  // Wipe + re-run the classifier. Tags clear immediately; the SSE
  // event fires again once the classifier returns.
  const retag = useCallback(async (recipeId: string) => {
    setRecipes((prev) => prev.map((r) => r.id === recipeId ? { ...r, tags: [] } : r));
    try { await api.recipesRetag(recipeId); }
    catch (e: any) { setError(e?.message || "retag failed"); }
  }, []);

  return { recipes, loading, error, refresh, get, create, update, remove, addItem, updateItem, deleteItem, setTags, retag };
}

// Stable hue derived from a recipe id — used as the placeholder cover
// color when imageUrl is empty. Same id → same hue across reloads.
export function hueFromId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return ((h % 360) + 360) % 360;
}
